import {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

import { PaginatedResult } from "@/interfaces/common/PaginatedResult";
import { OrderItem } from "@/interfaces/domain/Order";
import { Product } from "@/interfaces/domain/Product";
import { ProductCategory } from "@/interfaces/domain/ProductCategory";
import {
  ProductCategoryWithProducts,
  getCategoryListWithProductsRequest,
  getCategoryProductListRequest,
  getProductCategoryByIdRequest,
} from "@/services/api/productCategory";
import { logger } from "@/services/logger";
import { formatProductToOrderItem } from "@/utils/helpers";

export type ProductCategoryWithOrderItems = ProductCategory & { produtos: OrderItem[] };

export const useCategoryListWithProducts = (
  hallId: string,
  options?: UseQueryOptions<ProductCategoryWithProducts[]>
) => {
  const { data, isLoading } = useQuery<ProductCategoryWithProducts[]>({
    queryKey: [`/categories-with-products/${hallId}`],
    queryFn: () => getCategoryListWithProductsRequest(hallId),
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });

  return {
    data: data?.map((category) => ({
      ...category,
      produtos: category.produtos.map(formatProductToOrderItem),
    })) as ProductCategoryWithOrderItems[],
    isLoading,
  };
};

export const useProductsByCategory = ({
  categoryId,
  limit,
  options,
}: {
  categoryId: string;
  limit?: number;
  options?: UseInfiniteQueryOptions<PaginatedResult<Product>>;
}) => {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery<
    PaginatedResult<Product>
  >({
    queryKey: [`/category/${categoryId}/products`],
    queryFn: ({ pageParam = 1 }) =>
      getCategoryProductListRequest({ page: pageParam, categoryId, limit }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
    },
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
  return {
    data: data?.pages
      ?.map((page) => page.docs)
      .flat()
      .map(formatProductToOrderItem),
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
};

export const useCategoryById = (id: string, options?: UseQueryOptions<ProductCategory>) => {
  return useQuery<ProductCategory>({
    queryKey: [`/category-by-id/${id}`],
    queryFn: () => getProductCategoryByIdRequest(id),
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
};
